import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowUpRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profile = () => {
  const { user } = useAuth();
  const [bookmarks, setBookmarks] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bookmarksRes, submissionsRes] = await Promise.all([
          axios.get(`${API}/bookmarks`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/my-submissions`, { withCredentials: true }).catch(() => ({ data: [] }))
        ]);
        setBookmarks(bookmarksRes.data);
        setSubmissions(submissionsRes.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-ivory"><p className="label-editorial">Loading</p></div>;

  return (
    <div className="min-h-screen bg-ivory py-16 px-4 sm:px-6 lg:px-8" data-testid="profile-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-16 pb-12 border-b border-subtle">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-24 h-24 rounded-full object-cover" data-testid="profile-picture" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-burgundy text-ivory flex items-center justify-center text-3xl font-serif">
                {user?.name?.[0]}
              </div>
            )}
            <div className="flex-1">
              <p className="label-editorial mb-2">Member Profile</p>
              <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-2" data-testid="profile-name">{user?.name}</h1>
              <p className="text-charcoal-muted" data-testid="profile-email">{user?.email}</p>
              {user?.country && <p className="text-sm text-muted-warm font-mono mt-1">{user.country}</p>}
            </div>
            <Link to="/profile/edit" className="btn-outline-charcoal px-6 py-3" data-testid="edit-profile-button">
              Edit Profile
            </Link>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-16">
          {/* Bookmarks */}
          <div>
            <p className="label-editorial mb-6">Bookmarked Stories</p>
            {bookmarks.length > 0 ? (
              <div className="space-y-6">
                {bookmarks.slice(0, 5).map((article) => (
                  <Link key={article.article_id} to={`/articles/${article.slug}`} className="block pb-6 border-b border-subtle editorial-card group" data-testid={`bookmark-${article.article_id}`}>
                    <p className="text-xs font-mono uppercase tracking-widest text-terracotta mb-2">{article.category}</p>
                    <h3 className="font-serif text-xl text-charcoal group-hover:text-burgundy transition-colors mb-2">{article.title}</h3>
                    <p className="text-sm text-charcoal-muted line-clamp-2">{article.excerpt}</p>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-charcoal-muted">No bookmarked stories yet.</p>
            )}
          </div>

          {/* Submissions */}
          <div>
            <p className="label-editorial mb-6">My Submissions</p>
            {submissions.length > 0 ? (
              <div className="space-y-6">
                {submissions.slice(0, 5).map((sub) => (
                  <div key={sub.submission_id} className="pb-6 border-b border-subtle" data-testid={`submission-${sub.submission_id}`}>
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <h3 className="font-serif text-xl text-charcoal flex-1">{sub.title}</h3>
                      <span className={`text-xs font-mono uppercase tracking-widest px-2 py-1 ${sub.status === 'approved' ? 'bg-terracotta text-ivory' : sub.status === 'pending' ? 'bg-beige text-charcoal' : 'bg-charcoal text-ivory'}`}>
                        {sub.status}
                      </span>
                    </div>
                    <p className="text-sm text-charcoal-muted line-clamp-2">{sub.story}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div>
                <p className="text-charcoal-muted mb-4">You haven't submitted any stories yet.</p>
                <Link to="/submit-story" className="btn-burgundy px-6 py-3 inline-block">Share Your Story</Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
